import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { historyStatusColor } from "@lib/helpers/ColorHelper";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { UseMutationProps } from "@lib/types/Api";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { HistoryStatus } from "@lib/types/Billing";
import { TableColumnsProps } from "@lib/types/General";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TbWorldSearch } from "react-icons/tb";
import WalletAddressText from "@lib/components/MiniComponents/Others/WalletAddress";
import {
  CoinPriceTextFormat,
  CustomerLeadName,
  CustomerName,
} from "@lib/components/MiniComponents/Others";
import { SendBytePaymentRequest } from "@lib/types/SendByte";
import { AuthRoles } from "@lib/types";

type Props<T> = TableColumnsProps<T> & {
  deleteQuery: UseMutationProps<any>;
  addScanQuery: UseMutationProps<any>;
};

const getColumns = <T extends SendBytePaymentRequest>(
  props: Props<T>,
): ColumnsType<T> => {
  const { messages, deleteQuery, detailContext, addScanQuery, userRole } =
    props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      width: "160px",
      render: (id) => (
        <TableIdButton text={`${shortenString(id)}`} textToBeCopied={id} />
      ),
    },

    {
      title: "common.customerName",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "200px",
      render: (v, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },

    {
      title: "common.firstName",
      dataIndex: "firstName",
      width: "120px",
      filterType: "search",
      hidden: !!detailContext,
      render: (v, r) => (
        <CustomerLeadName customerLead={r?.customerUser} link />
      ),
    },

    {
      title: "common.walletAddress",
      dataIndex: "sendByteWalletAddress.address",
      width: "230px",
      render: (_, r) => (
        <WalletAddressText
          walletAddress={r?.sendByteWalletAddress}
          showNetwork={true}
          shortenAddress={20}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.amount",
      dataIndex: "extraData.scanned.lastTxAmount",
      width: "185px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={r?.extraData?.scanned?.lastTxAmount}
          currency={r?.network}
          showCurrency={r?.network}
          showCurrencyImage
        />
      ),
      filterType: "search",
    },

    {
      title: "common.status",
      dataIndex: "status",
      width: "150px",
      render: (value) => (
        <Tag color={historyStatusColor(value)}>
          {(messages["enums.billingHistory"][value] as string) || value}
        </Tag>
      ),
      filterType: "dropdown",
      filters: Object.values(HistoryStatus).map((value) => ({
        text: messages["enums.billingHistory"][value] || value,
        value: value,
      })),
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).fromNow(),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="scan"
            onClick={() => addScanQuery.mutate(record)}
            noLabel
            loading={addScanQuery.isPending}
            disabled={
              record.status == HistoryStatus.Success || addScanQuery.isPending
            }
          />

          <ActionButton
            type="link"
            targetBlank
            icon={<TbWorldSearch />}
            href={record?.sendByteWalletAddress?.extraData?.viewUrl || null}
            disabled={
              !record?.sendByteWalletAddress?.extraData?.viewUrl || null
            }
            label={messages["common.blockChain"] as string}
            showBorder
          />

          {userRole === AuthRoles.Admin && (
            <ActionButton
              type="delete"
              onClick={() => deleteQuery.mutate(record)}
              noLabel
              itemName="sidebar.blockchainPaymentRequest"
              disabled={
                record.status == HistoryStatus.Success || deleteQuery.isPending
              }
            />
          )}
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
